import React from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';

import Button from '@dashboard/components/Button';
import ConnectStripeDialog from '@dashboard/components/ConnectStripeDialog';
import { Progress } from '@dashboard/components/ui/progress';
import useSetupSteps from '@dashboard/hooks/useSetupSteps';

function SetupSkeleton() {
  return (
    <div className="max-w-xl w-full mx-auto py-12 px-4 animate-pulse">
      <div className="text-center mb-10">
        <div className="h-7 w-40 bg-gray-100 dark:bg-muted rounded mx-auto mb-2" />
        <div className="h-4 w-72 bg-gray-100 dark:bg-muted rounded mx-auto mb-5" />
        <div className="w-full max-w-sm mx-auto">
          <div className="h-1.5 bg-gray-100 dark:bg-muted rounded-full" />
        </div>
      </div>

      <div>
        {Array.from({ length: 8 }).map((_, index) => (
          <div
            key={index}
            className={`flex items-start gap-4 px-5 py-5 ${
              index < 7 ? 'border-b border-gray-100 dark:border-border' : ''
            }`}
          >
            <div className="h-8 w-8 flex-shrink-0 rounded-full bg-gray-100 dark:bg-muted mt-0.5" />
            <div className="flex-1 min-w-0 w-full pt-1.5">
              <div className="h-3.5 w-3/5 bg-gray-100 dark:bg-muted rounded mb-2" />
              <div className="h-3 w-4/5 bg-gray-100 dark:bg-muted rounded" />
            </div>
            <div className="h-9 w-36 flex-shrink-0 rounded-md bg-gray-100 dark:bg-muted mt-0.5" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Setup() {
  const { steps, completedCount, totalCount, isLoading } = useSetupSteps();

  //return <SetupSkeleton />;
  if (isLoading) return <SetupSkeleton />;

  return (
    <div className="max-w-xl w-full mx-auto py-12 px-4">
      <div className="text-center mb-10">
        <h1 className="text-lg font-semibold mb-1">Get started</h1>
        <p className="text-gray-500 dark:text-muted-foreground mb-5">
          Complete these steps to start accepting payments
        </p>
        <Progress
          value={totalCount > 0 ? (completedCount / totalCount) * 100 : 0}
          className="max-w-sm mx-auto"
        />
      </div>

      <div>
        {steps.map((step, index) => (
          <div
            key={step.title}
            className={`flex items-start gap-4 px-5 py-5 ${
              index < steps.length - 1 ? 'border-b border-gray-100 dark:border-border' : ''
            }`}
          >
            <div className={`flex-shrink-0 pt-0.5 ${step.completed ? 'opacity-40' : ''}`}>
              <div className="h-8 w-8 rounded-full bg-gray-100 dark:bg-muted flex items-center justify-center text-sm text-gray-500 dark:text-muted-foreground font-medium">
                {index + 1}
              </div>
            </div>

            <div className={`flex-1 min-w-0 pt-1 ${step.completed ? 'opacity-40' : ''}`}>
              <div className="font-medium text-gray-900 dark:text-foreground leading-snug">
                {step.title}
              </div>
              <div className="text-sm text-gray-500 dark:text-muted-foreground mt-0.5">
                {step.description}
              </div>
            </div>

            <div className="flex-shrink-0 pt-0.5">
              {step.loading ? (
                <div className="h-8 w-8 rounded-full bg-gray-100 dark:bg-muted animate-pulse" />
              ) : step.completed ? (
                <div className="h-8 w-8 rounded-full bg-green-50 dark:bg-green-500/15 flex items-center justify-center">
                  <CheckIcon className="h-4 w-4 text-green-600 dark:text-green-500 stroke-[2.5]" />
                </div>
              ) : step.link === '/settings/stripe' ? (
                <ConnectStripeDialog
                  trigger={
                    <Button appearance="secondary" size="base" className="whitespace-nowrap">
                      {step.action}
                    </Button>
                  }
                />
              ) : (
                <Button
                  href={step.link}
                  target={step.target}
                  onClick={step.onClick}
                  appearance="secondary"
                  size="base"
                  className="whitespace-nowrap"
                >
                  {step.action}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
